/**
 * @file GameEnvironment.ts
 * @role Reads build/runtime environment flags into one typed object so the rest of the app
 *       never touches import.meta.env directly.
 * @owns GameEnvironment type, build-time constants, and readGameEnvironment().
 * @reads import.meta.env (Vite).
 * @neverDoes Decide game outcomes. The mock scenario only selects which deterministic
 *            mock result the dev/test fake server returns.
 * @invariants 6 (mocks are dev/test only).
 * @see docs/BACKEND_CONTRACT.md
 */
export interface GameEnvironment {
  isDev: boolean;
  /** True only for the bundled, test-only `vite --mode mock-preview` artifact. */
  isMockPreview: boolean;
  /** Which deterministic fake-server scenario to use in dev/test (src/mocks/rgsScenarios.ts). */
  mockScenario: string;
  /** Overrides game.config.json's `rgs.baseUrl` — the one knob that moves the game between a
   *  local/mocked RGS and the deployed one. Empty string means "use the config value". */
  rgsBaseUrl: string;
  /** Raw `VITE_RGS_MODE` — which backend this build talks to (`mock` | `msw` | `live`).
   *  Empty means "use game.config.json's `mockRgs.enabled` default". Interpreted only by
   *  `resolveRgsMode`, which also enforces the dev/test-only guard (Invariant 6). */
  rgsMode: string;
  /** Build-selected presentation package. Unknown values safely fall back in SkinRegistry. */
  gameSkinId: string;
  /** Dev-only visual inspection route requested through `?skinPreview=1`. */
  skinPreviewRequested: boolean;
  /** Dev-only @pixi/ui widget showcase route requested through `?uiPreview=1`. */
  uiPreviewRequested: boolean;
  /** Read-only invariant probe available only in the explicit mock-preview artifact. */
  mockPreviewProbeEnabled: boolean;
  /**
   * Dev/mock-preview-only `?fsConsent=reset`: ignore any stored fullscreen answer for this
   * load, so the consent modal can be retested without clearing site data by hand.
   */
  fullscreenConsentReset: boolean;
}

/** Statically replaced by Vite so DEV-only dynamic imports remain removable in production. */
export const IS_DEV_BUILD = import.meta.env.DEV;
export const IS_MOCK_PREVIEW_BUILD = import.meta.env.MODE === 'mock-preview';

export function readGameEnvironment(search = readLocationSearch()): GameEnvironment {
  const env = import.meta.env;
  const params = new URLSearchParams(search);
  return {
    isDev: IS_DEV_BUILD,
    isMockPreview: IS_MOCK_PREVIEW_BUILD,
    mockScenario: env.VITE_MOCK_SCENARIO ?? '',
    rgsBaseUrl: env.VITE_RGS_BASE_URL ?? '',
    rgsMode: env.VITE_RGS_MODE ?? '',
    gameSkinId: env.VITE_GAME_SKIN ?? '',
    skinPreviewRequested: IS_DEV_BUILD && params.get('skinPreview') === '1',
    uiPreviewRequested: IS_DEV_BUILD && params.get('uiPreview') === '1',
    mockPreviewProbeEnabled: IS_MOCK_PREVIEW_BUILD,
    fullscreenConsentReset:
      (IS_DEV_BUILD || IS_MOCK_PREVIEW_BUILD) && params.get('fsConsent') === 'reset',
  };
}

function readLocationSearch(): string {
  return typeof location === 'undefined' ? '' : location.search;
}
